import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCampaignReport, getStatusData } from '../../../lib/api';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './summary.css';
import { ERROR_CODE_MESSAGES } from '../../../constants/errorCodeMessages';
import './templateReport.css';

const useQuery = () => new URLSearchParams(useLocation().search);

const TemplateReport = () => {
  const query = useQuery();
  const campaignId = query.get('campaign_id');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusResp, setStatusResp] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    setError('');
    getCampaignReport(campaignId)
      .then((res) => setData(res))
      .catch((e) => setError(e?.message || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const summary = data?.summary || data || {};
  const messages = data?.messages || [];

  const countFor = (key) => {
    const rec = messages.find((m) => m.status === key);
    return rec ? (rec.count ?? 0) : 0;
  };
  const failedCount = useMemo(() => {
    const wa = messages.find((m) => typeof m.failed_count !== 'undefined');
    return wa ? wa.failed_count : 0;
  }, [messages]);

  const chartData = useMemo(() => ({
    labels: ['Sent', 'Delivered', 'Read', 'Failed', 'Webhook Failed', 'WA Failed'],
    datasets: [
      {
        label: 'Count',
        data: [
          countFor('sent'),
          countFor('delivered'),
          countFor('read'),
          countFor('failed'),
          countFor('created'),
          failedCount,
        ],
        backgroundColor: [
          '#F59E0B', // sent
          '#3B82F6', // delivered
          '#10B981', // read
          '#EF4444', // failed
          '#D97706', // webhook failed (created)
          '#8B5CF6', // wa failed
        ].map(c => c + 'CC'),
        borderColor: ['#B45309','#1D4ED8','#059669','#DC2626','#92400E','#6D28D9'],
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  }), [messages, failedCount]);
  const chartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: { mode: 'nearest', intersect: false },
    },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.06)' } },
      y: { grid: { display: false } },
    },
  }), []);

  const totalAudience = useMemo(() => {
    const t = messages.find((m) => typeof m.total_count !== 'undefined');
    return t ? t.total_count : (summary.total_count ?? 0);
  }, [messages, summary]);

  const amount = useMemo(() => {
    const a = messages.find((m) => typeof m.amount !== 'undefined');
    return a ? a.amount : (summary.amount ?? 0);
  }, [messages, summary]);

  const refund = useMemo(() => {
    const r = messages.find((m) => typeof m.refund !== 'undefined');
    return r ? r.refund : (summary.refund ?? 0);
  }, [messages, summary]);

  const loadStatus = async (status, p = 1) => {
    if (!campaignId) return;
    setSelectedStatus(status);
    setStatusLoading(true);
    setStatusError('');
    try {
      const resp = await getStatusData(campaignId, status, p, limit);
      setStatusResp(resp);
      setPage(p);
    } catch (e) {
      setStatusError(e?.message || 'Failed to load status data');
      setStatusResp(null);
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className="summary-wrap">
      <div className="summary-header">
        <h2>Campaign Report</h2>
        <div className="summary-meta">Campaign ID: {campaignId || '—'}</div>
      </div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <div className="summary-scroll">
          <div className="trp-content">
            <div className="kpi-row">
              {Array.from({length:3}).map((_,i)=> (
                <div className="kpi-card" key={`k${i}`}>
                  <div className="sk sk-text long" style={{ height: 18 }} />
                  <div className="sk sk-text" style={{ height: 28, width: 120, marginTop: 8 }} />
                </div>
              ))}
            </div>
            <div className="grid-2">
              <div className="panel"><div className="panel-body"><div className="sk" style={{ height: 280, borderRadius: 12 }} /></div></div>
              <div className="panel"><div className="panel-body">
                {Array.from({length:6}).map((_,i)=> (
                  <div key={`sc${i}`} className="sk" style={{ height: 56, borderRadius: 12, marginBottom: 10 }} />
                ))}
              </div></div>
            </div>
            <div className="panel"><div className="panel-body"><div className="sk" style={{ height: 220, borderRadius: 12 }} /></div></div>
          </div>
        </div>
      ) : (
        <div className="summary-scroll">
          <div className="trp-content">
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-title">Spending</div>
                <div className="kpi-value">₹{amount}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Total Audience</div>
                <div className="kpi-value">{totalAudience}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Refund</div>
                <div className="kpi-value">₹{refund}</div>
              </div>
            </div>

            <div className="grid-2">
              <div className="panel">
                <div className="panel-title">Status Distribution</div>
                <div className="panel-body" style={{ height: 320 }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
              <div className="panel">
                <div className="panel-title">Statuses</div>
                <div className="status-cards">
                  {[
                    { key: 'sent', label: 'Sent', value: countFor('sent'), color: '#F59E0B' },
                    { key: 'delivered', label: 'Delivered', value: countFor('delivered'), color: '#3B82F6' },
                    { key: 'read', label: 'Read', value: countFor('read'), color: '#10B981' },
                    { key: 'failed', label: 'Failed', value: countFor('failed'), color: '#EF4444' },
                    { key: 'created', label: 'Webhook Failed', value: countFor('created'), color: '#D97706' },
                    { key: 'failed_count', label: 'WA Failed', value: failedCount, color: '#8B5CF6' },
                  ].map((c) => (
                    <button
                      key={c.key}
                      className={`status-card ${selectedStatus === c.key ? 'active' : ''}`}
                      style={{ borderColor: c.color }}
                      onClick={() => loadStatus(c.key, 1)}
                    >
                      <div className="status-title">{c.label}</div>
                      <div className="status-value" style={{ color: c.color }}>{c.value}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-title">{selectedStatus ? `Details • ${selectedStatus}` : 'Details'}</div>
              {statusError && <div className="error" style={{ marginBottom: 8 }}>{statusError}</div>}
              {statusLoading && <div className="summary-loading">Loading…</div>}
              {!statusLoading && statusResp && (
                <div className="status-table-container">
                  <table className="status-table">
                    <thead>
                      <tr>
                        <th>Recipient ID</th>
                        <th>Status Timestamp</th>
                        <th>Updated At</th>
                        {!['read','delivered','sent'].includes(selectedStatus) && <th>Errors</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(statusResp.messages || []).map((m, i) => (
                        <tr key={i}>
                          <td>{m.recipient_id}</td>
                          <td>{m.status_timestamp ? new Date(m.status_timestamp).toLocaleString() : '—'}</td>
                          <td>{m.updated_at ? new Date(m.updated_at).toLocaleString() : '—'}</td>
                          {!['read','delivered','sent'].includes(selectedStatus) && (
                            <td>
                              {(() => {
                                if (!m.errors) return '—';
                                try {
                                  const parsed = typeof m.errors === 'string' ? JSON.parse(m.errors) : m.errors;
                                  const first = Array.isArray(parsed) ? parsed[0] : parsed;
                                  const code = first?.code || first?.title || first?.error_code;
                                  const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
                                  const mapped = ERROR_CODE_MESSAGES[codeNum];
                                  return mapped || first?.message || first?.title || 'Unknown error';
                                } catch {
                                  return String(m.errors);
                                }
                              })()}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="pagination-controls">
                    <button disabled={page <= 1} onClick={() => loadStatus(selectedStatus, page - 1)}>Previous</button>
                    <span>
                      Page {page} of {statusResp.totalPages || statusResp.pagination?.total_pages || page}
                    </span>
                    <button
                      disabled={(() => {
                        const total = statusResp.totalPages || statusResp.pagination?.total_pages;
                        if (total) return page >= total;
                        return (statusResp.messages || []).length < limit;
                      })()}
                      onClick={() => loadStatus(selectedStatus, page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateReport;
